import { useRef, useState } from "react";

import EditIcon from "./common/EditIcon";
import TrashIcon from "./common/TrashIcon";
import {
  createAttachmentName,
  getContractAttachmentType,
} from "../utils/attachmentNaming";

const packageOptions = ["1 Mini", "2 Kickstarter", "3 Basic", "4 Grow"];
const billingPeriods = ["woechentlich", "halb monatlich", "monatlich"];
const customPriceFields = [
  "Setup",
  "IT Kosten",
  "Stammdatenpflege",
  "WE ohne Pruefung Palette sortenrein",
  "WE ohne Pruefung Palette mischpalette",
  "WE ohne Pruefung Paket sortenrein",
  "WE ohne Pruefung Paket mischkarton",
  "WE mit Pruefung 10-100%",
  "Warenausgang B2C Sendung",
  "Warenausgang B2B Sendung",
  "Leistung nach Zeitaufwand Lager",
  "Leistung nach Zeitaufwand Verwaltung",
  "Leistung nach Zeitaufwand IT/EDV Mitarbeiter",
];
const shippingProviders = ["DPD", "DHL", "GLS"];
const emptyShipping = { label: "", zone: "national", weightUntil: "", price: "" };
const emptyCarton = { label: "", wallType: "einwellig", dimensions: "", price: "" };

const emptyForm = {
  id: "",
  short: "",
  customerNumber: "",
  name: "",
  contact: "",
  phone: "",
  address: "",
  street: "",
  houseNumber: "",
  zipCode: "",
  city: "",
  country: "",
  generalEmail: "",
  billingEmails: [""],
  billingPeriod: "monatlich",
  isPackageCustomer: false,
  packageType: "",
  packagePrices: {},
  bankName: "",
  iban: "",
  bic: "",
  contractTimestamp: "",
  contractStartDate: "",
  contractEndDate: "",
  contractDuration: "",
  contractFiles: [],
  customPrices: {},
  cartonPrices: [{ ...emptyCarton }],
  shippingPrices: {
    DPD: [{ ...emptyShipping }],
    DHL: [{ ...emptyShipping }],
    GLS: [{ ...emptyShipping }],
  },
  miscBillingData: [{ label: "", value: "" }],
  additionalContractPositions: [],
  bioMode: "nein",
  bio: false,
  tracesImport: false,
  certificateData: "",
  certificateFileName: "BIO Zertifikat.pdf",
  certificateValidUntil: "",
  otherFileData: "",
  otherFileName: "",
  createdAt: "",
};

function readPdfAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== "application/pdf") {
      reject(new Error("Bitte waehle eine PDF-Datei aus."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Die PDF-Datei konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

function downloadDataUrl(dataUrl, fileName) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName || "datei.pdf";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getSupplierAttachmentName(supplier, attachmentType, dataUrl, sequence = 1) {
  return createAttachmentName({
    area: "Kunde",
    processNumber: supplier.processNumber,
    attachmentType,
    sequence,
    dataUrl,
    extension: "pdf",
  });
}

function getContractSequence(files, index) {
  const type = getContractAttachmentType(files[index]?.label);
  return files
    .slice(0, index + 1)
    .filter((file) => getContractAttachmentType(file.label) === type).length;
}

function getBioLabel(supplier) {
  if (supplier?.bioMode === "bio_und_nicht_bio") return "Bio und nicht bio";
  if (supplier?.bioMode === "bio" || supplier?.bio) return "Ja";
  return "Nein";
}

export function normalizeForm(supplier = {}) {
  return {
    ...emptyForm,
    ...supplier,
    generalEmail: supplier.generalEmail || supplier.phone || "",
    billingEmails: Array.isArray(supplier.billingEmails) && supplier.billingEmails.length
      ? supplier.billingEmails
      : [""],
    isPackageCustomer: Boolean(supplier.isPackageCustomer),
    packagePrices: supplier.packagePrices || {},
    contractFiles: Array.isArray(supplier.contractFiles) ? supplier.contractFiles : [],
    customPrices: supplier.customPrices || {},
    cartonPrices: Array.isArray(supplier.cartonPrices) && supplier.cartonPrices.length
      ? supplier.cartonPrices
      : [{ ...emptyCarton }],
    shippingPrices: {
      DPD: supplier.shippingPrices?.DPD?.length ? supplier.shippingPrices.DPD : [{ ...emptyShipping }],
      DHL: supplier.shippingPrices?.DHL?.length ? supplier.shippingPrices.DHL : [{ ...emptyShipping }],
      GLS: supplier.shippingPrices?.GLS?.length ? supplier.shippingPrices.GLS : [{ ...emptyShipping }],
    },
    miscBillingData: Array.isArray(supplier.miscBillingData) && supplier.miscBillingData.length
      ? supplier.miscBillingData
      : [{ label: "", value: "" }],
    additionalContractPositions: Array.isArray(supplier.additionalContractPositions)
      ? supplier.additionalContractPositions
      : [],
    bioMode: supplier.bioMode || (supplier.bio ? "bio" : "nein"),
    bio: supplier.bioMode ? supplier.bioMode !== "nein" : Boolean(supplier.bio),
  };
}

export function filterSuppliers(suppliers, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return suppliers;

  return suppliers.filter((supplier) =>
    [supplier.name, supplier.short, supplier.customerNumber, supplier.contact]
      .some((value) => String(value || "").toLowerCase().includes(query))
  );
}

function requiredClass(value) {
  return value ? "field" : "field field-required-missing";
}

function isValidIban(value) {
  const iban = String(value || "").replace(/\s/g, "").toUpperCase();
  if (!iban) return true;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const code = char >= "A" && char <= "Z" ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of code) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder === 1;
}

export default function Lieferantenverwaltung({
  suppliers,
  onSaveSupplier,
  onDeleteSupplier,
}) {
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [activeSection, setActiveSection] = useState("master");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showMoreDocuments, setShowMoreDocuments] = useState(false);
  const certificateInputRef = useRef(null);
  const otherFileInputRef = useRef(null);

  const setArrayItem = (field, index, key, value) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addArrayItem = (field, item) => {
    setForm((current) => ({ ...current, [field]: [...current[field], item] }));
  };

  const removeArrayItem = (field, index, fallbackItem) => {
    setForm((current) => {
      const nextItems = current[field].filter((item, itemIndex) => itemIndex !== index);
      return { ...current, [field]: nextItems.length ? nextItems : [fallbackItem] };
    });
  };

  const setShippingItem = (provider, index, key, value) => {
    setForm((current) => ({
      ...current,
      shippingPrices: {
        ...current.shippingPrices,
        [provider]: current.shippingPrices[provider].map((item, itemIndex) =>
          itemIndex === index ? { ...item, [key]: value } : item
        ),
      },
    }));
  };

  const removeShippingItem = (provider, index) => {
    setForm((current) => {
      const nextItems = current.shippingPrices[provider].filter((item, itemIndex) => itemIndex !== index);
      return {
        ...current,
        shippingPrices: {
          ...current.shippingPrices,
          [provider]: nextItems.length ? nextItems : [{ ...emptyShipping }],
        },
      };
    });
  };

  const handleSave = async () => {
    const short = (form.short || "").trim();
    const customerNumber = (form.customerNumber || "").trim();
    const name = (form.name || "").trim();
    const contact = (form.contact || "").trim();
    const phone = (form.generalEmail || form.phone || "").trim();
    const street = (form.street || "").trim();
    const houseNumber = (form.houseNumber || "").trim();
    const zipCode = (form.zipCode || "").trim();
    const city = (form.city || "").trim();
    const country = (form.country || "").trim();
    const address = [street && houseNumber ? `${street} ${houseNumber}` : `${street}${houseNumber}`, [zipCode, city].filter(Boolean).join(" "), country]
      .filter(Boolean)
      .join(", ") || (form.address || "").trim();
    const otherFileName = (form.otherFileName || "").trim();

    if (!short || !name) {
      window.alert("Bitte Kuerzel und Kundenname ausfuellen.");
      return;
    }

    if (form.otherFileData && !otherFileName) {
      window.alert("Bitte benenne die sonstige PDF-Datei.");
      return;
    }

    try {
      const savedSupplier = await onSaveSupplier({
        ...form,
        id: editId || "",
        short,
        customerNumber,
        name,
        contact,
        phone,
        generalEmail: phone,
        address,
        street,
        houseNumber,
        zipCode,
        city,
        country,
        billingEmails: form.billingEmails.map((mail) => mail.trim()).filter(Boolean),
        bioMode: form.bioMode,
        bio: form.bioMode !== "nein",
        tracesImport: Boolean(form.tracesImport),
        certificateFileName: "BIO Zertifikat.pdf",
        otherFileName,
        createdAt: form.createdAt || new Date().toLocaleDateString("de-DE"),
      });

      if (savedSupplier) {
        setForm(normalizeForm(savedSupplier));
        setEditId(savedSupplier.id);
      }
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleEdit = (supplier) => {
    setForm(normalizeForm(supplier));
    setEditId(supplier.id);
    setActiveSection("master");
  };

  const resetCustomerForm = (targetSection = "master") => {
    setForm(emptyForm);
    setEditId(null);
    setShowMoreDocuments(false);
    setActiveSection(targetSection);
  };

  const handleDelete = async (supplierId) => {
    try {
      await onDeleteSupplier(supplierId);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handlePdfUpload = async (event, fieldName) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readPdfAsDataUrl(file);
      setForm((currentForm) => ({
        ...currentForm,
        [fieldName]: dataUrl,
        ...(fieldName === "otherFileData" && !currentForm.otherFileName
          ? { otherFileName: file.name }
          : {}),
      }));
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  const handleContractUpload = async (event, defaultLabel) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await readPdfAsDataUrl(file);
      setForm((current) => ({
        ...current,
        contractFiles: [
          ...current.contractFiles,
          { label: defaultLabel, fileName: file.name, data: dataUrl },
        ],
      }));
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  return (
    <div className="page-stack">
      <div className="section-header-row">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="page-title">Kundenverwaltung</h1>
          <p className="section-text">
            Neue Kunden erscheinen nach dem Speichern sofort in der Erfassung.
          </p>
        </div>
        {editId && <div className="customer-active-summary">
          <strong>Aktiver Kunde:</strong>{" "}
          {`${form.name} | ${form.short}${form.customerNumber ? ` | JTL ${form.customerNumber}` : ""}`}
        </div>}
      </div>

      <nav className="customer-section-nav" aria-label="Bereiche der Kundenverwaltung">
        {[
          ["master", "Kunde bearbeiten"],
          ["customers", "Kunden"],
          ["contract", "Vertragsdetails"],
          ["shipping", "Versand"],
          ["cartons", "Kartonagen"],
        ].map(([section, label]) => {
          const requiresCustomer = ["contract", "shipping", "cartons"].includes(section);
          return (
            <button
              key={section}
              type="button"
              disabled={requiresCustomer && !editId}
              title={requiresCustomer && !editId ? "Bitte den neuen Kunden zuerst speichern." : ""}
              onClick={() => setActiveSection(section)}
              className={`button ${activeSection === section ? "button-primary" : "button-secondary"}`}
            >
              {label}
            </button>
          );
        })}
        {editId && (
          <button
            type="button"
            className="button button-secondary"
            onClick={() => resetCustomerForm("master")}
          >
            Neuer Kunde
          </button>
        )}
      </nav>

      {!editId && activeSection === "master" && (
        <p className="message message-info">
          Vertragsdetails, Versand und Kartonagen sind nach dem ersten Speichern verfügbar.
        </p>
      )}

      {activeSection !== "customers" && (
      <section className="card">
        <h2>{editId ? "Kunden bearbeiten" : "Neuen Kunden anlegen"}</h2>

        <div className="form-grid columns-2">
          {activeSection === "master" && (
          <>
          <label className={requiredClass(form.short.trim())}>
            <span>Kuerzel *</span>
            <input value={form.short} onChange={(event) => setForm({ ...form, short: event.target.value })} />
          </label>
          <label className={requiredClass(form.customerNumber.trim())}>
            <span>Kundennummer JTL *</span>
            <input value={form.customerNumber} onChange={(event) => setForm({ ...form, customerNumber: event.target.value })} />
          </label>
          <label className={requiredClass(form.name.trim())}>
            <span>Name Firma *</span>
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label className={requiredClass(form.contact.trim())}>
            <span>Ansprechpartner *</span>
            <input value={form.contact} onChange={(event) => setForm({ ...form, contact: event.target.value })} />
          </label>
          <label className={requiredClass(form.generalEmail.trim())}>
            <span>Email allgemein *</span>
            <input type="email" value={form.generalEmail} onChange={(event) => setForm({ ...form, generalEmail: event.target.value, phone: event.target.value })} />
          </label>
          <label className="field">
            <span>Bank Name</span>
            <input value={form.bankName} onChange={(event) => setForm({ ...form, bankName: event.target.value })} />
          </label>
          <label className={form.iban && !isValidIban(form.iban) ? "field field-required-missing" : "field"}>
            <span>IBAN</span>
            <input value={form.iban} onChange={(event) => setForm({ ...form, iban: event.target.value })} />
          </label>
          <label className="field">
            <span>BIC</span>
            <input value={form.bic} onChange={(event) => setForm({ ...form, bic: event.target.value })} />
          </label>
          <label className={requiredClass(form.street.trim())}>
            <span>Strasse *</span>
            <input value={form.street} onChange={(event) => setForm({ ...form, street: event.target.value })} />
          </label>
          <label className={requiredClass(form.houseNumber.trim())}>
            <span>Hausnummer *</span>
            <input value={form.houseNumber} onChange={(event) => setForm({ ...form, houseNumber: event.target.value })} />
          </label>
          <label className={requiredClass(form.zipCode.trim())}>
            <span>PLZ *</span>
            <input value={form.zipCode} onChange={(event) => setForm({ ...form, zipCode: event.target.value })} />
          </label>
          <label className={requiredClass(form.city.trim())}>
            <span>Ort *</span>
            <input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
          </label>
          <label className={requiredClass(form.country.trim())}>
            <span>Land *</span>
            <input value={form.country} onChange={(event) => setForm({ ...form, country: event.target.value })} />
          </label>

          <label className="field">
            <span>BIO-Kennzeichnung</span>
            <select aria-label="BIO-Kennzeichnung" value={form.bioMode} onChange={(event) => setForm({ ...form, bioMode: event.target.value, bio: event.target.value !== "nein" })}>
              <option value="bio">BIO</option>
              <option value="nein">Nicht BIO</option>
              <option value="bio_und_nicht_bio">BIO und Nicht BIO</option>
            </select>
          </label>

          <label className="field">
            <span>Anlage Zeitstempel</span>
            <input type="datetime-local" value={form.contractTimestamp} onChange={(event) => setForm({ ...form, contractTimestamp: event.target.value })} />
          </label>
          </>
          )}

          {activeSection === "contract" && (
          <>
          <label className="field">
            <span>Vertragsbeginn</span>
            <input type="date" value={form.contractStartDate} onChange={(event) => setForm({ ...form, contractStartDate: event.target.value })} />
          </label>
          <label className="field">
            <span>Vertragsende</span>
            <input type="date" value={form.contractEndDate} onChange={(event) => setForm({ ...form, contractEndDate: event.target.value })} />
          </label>
          <label className="field">
            <span>Vertragslaufzeit</span>
            <input placeholder="z.B. 12 Monate" value={form.contractDuration} onChange={(event) => setForm({ ...form, contractDuration: event.target.value })} />
          </label>

          <div className={`${requiredClass(form.billingEmails.some((mail) => mail.trim()))} field-wide`}>
            <span>Rechnungs Mail *</span>
            <div className="compact-table">
              {form.billingEmails.map((mail, index) => (
                <div className="compact-row compact-row-mail" key={index}>
                  <input
                    type="email"
                    value={mail}
                    placeholder="rechnung@example.de"
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        billingEmails: current.billingEmails.map((item, itemIndex) =>
                          itemIndex === index ? event.target.value : item
                        ),
                      }))
                    }
                  />
                  <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => setForm((current) => ({ ...current, billingEmails: current.billingEmails.filter((item, itemIndex) => itemIndex !== index).length ? current.billingEmails.filter((item, itemIndex) => itemIndex !== index) : [""] }))}><TrashIcon /></button>
                </div>
              ))}
            </div>
            <button type="button" className="button button-secondary" onClick={() => setForm((current) => ({ ...current, billingEmails: [...current.billingEmails, ""] }))}>
              + Mail
            </button>
          </div>

          <label className="field">
            <span>Abrechnungszeitraum *</span>
            <select value={form.billingPeriod} onChange={(event) => setForm({ ...form, billingPeriod: event.target.value })}>
              {billingPeriods.map((period) => <option key={period} value={period}>{period}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Paketkunde *</span>
            <select value={form.isPackageCustomer ? "ja" : "nein"} onChange={(event) => setForm({ ...form, isPackageCustomer: event.target.value === "ja" })}>
              <option value="nein">Nein</option>
              <option value="ja">Ja</option>
            </select>
          </label>

          {form.isPackageCustomer ? (
            <>
              <label className="field">
                <span>Paket</span>
                <select value={form.packageType} onChange={(event) => setForm({ ...form, packageType: event.target.value })}>
                  <option value="">Bitte waehlen</option>
                  {packageOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              {packageOptions.map((option) => (
                <label key={option} className="field">
                  <span>Preis {option}</span>
                  <input type="number" min="0" step="0.01" value={form.packagePrices[option] || ""} onChange={(event) => setForm({ ...form, packagePrices: { ...form.packagePrices, [option]: event.target.value } })} />
                </label>
              ))}
            </>
          ) : (
            customPriceFields.map((field) => (
              <label key={field} className="field">
                <span>{field}</span>
                <input type="number" min="0" step="0.01" value={form.customPrices[field] || ""} onChange={(event) => setForm({ ...form, customPrices: { ...form.customPrices, [field]: event.target.value } })} />
              </label>
            ))
          )}

          <div className="field field-wide">
            <span>Weitere Vertragspositionen</span>
            <div className="compact-table">
              {form.additionalContractPositions.map((row, index) => (
                <div className="compact-row compact-row-mail" key={index}>
                  <input placeholder="Bezeichnung" value={row.label || ""} onChange={(event) => setArrayItem("additionalContractPositions", index, "label", event.target.value)} />
                  <input placeholder="Wert" value={row.value || ""} onChange={(event) => setArrayItem("additionalContractPositions", index, "value", event.target.value)} />
                  <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => setForm((current) => ({ ...current, additionalContractPositions: current.additionalContractPositions.filter((item, itemIndex) => itemIndex !== index) }))}><TrashIcon /></button>
                </div>
              ))}
            </div>
            <button type="button" className="button button-secondary" onClick={() => addArrayItem("additionalContractPositions", { label: "", value: "" })}>+ Zeile</button>
          </div>
          </>
          )}

          {activeSection === "cartons" && (
          <div className="field field-wide">
            <span>Kartonagen Preise</span>
            <div className="compact-table">
              <div className="compact-row compact-row-carton compact-head">
                <span>Bezeichnung</span>
                <span>Art</span>
                <span>Masse LxBxH</span>
                <span>Preis</span>
                <span></span>
              </div>
              {form.cartonPrices.map((row, index) => (
                <div className="compact-row compact-row-carton" key={index}>
                  <input placeholder="Bezeichnung" value={row.label} onChange={(event) => setArrayItem("cartonPrices", index, "label", event.target.value)} />
                  <select value={row.wallType || "einwellig"} onChange={(event) => setArrayItem("cartonPrices", index, "wallType", event.target.value)}>
                    <option value="einwellig">Einwellig</option>
                    <option value="zweiwellig">Zweiwellig</option>
                  </select>
                  <input placeholder="LxBxH cm" value={row.dimensions || ""} onChange={(event) => setArrayItem("cartonPrices", index, "dimensions", event.target.value)} />
                  <input type="number" min="0" step="0.01" placeholder="Preis" value={row.price} onChange={(event) => setArrayItem("cartonPrices", index, "price", event.target.value)} />
                  <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => removeArrayItem("cartonPrices", index, { ...emptyCarton })}><TrashIcon /></button>
                </div>
              ))}
            </div>
            <button type="button" className="button button-secondary" onClick={() => addArrayItem("cartonPrices", { ...emptyCarton })}>+ Kartonage</button>
          </div>
          )}

          {activeSection === "shipping" && (
          <>
          {shippingProviders.map((provider) => (
            <div className="field field-wide" key={provider}>
              <span>Porto Kosten {provider}</span>
              <div className="compact-table">
                <div className="compact-row compact-row-shipping compact-head">
                  <span>Bezeichnung</span>
                  <span>National/International</span>
                  <span>Gewicht bis</span>
                  <span>Preis</span>
                  <span></span>
                </div>
                {form.shippingPrices[provider].map((row, index) => (
                  <div className="compact-row compact-row-shipping" key={index}>
                    <input placeholder="Bezeichnung" value={row.label} onChange={(event) => setShippingItem(provider, index, "label", event.target.value)} />
                    <select value={row.zone} onChange={(event) => setShippingItem(provider, index, "zone", event.target.value)}>
                      <option value="national">National</option>
                      <option value="international">International</option>
                    </select>
                    <input type="number" min="0" step="0.01" placeholder="Gewicht bis" value={row.weightUntil} onChange={(event) => setShippingItem(provider, index, "weightUntil", event.target.value)} />
                    <input type="number" min="0" step="0.01" placeholder="Preis" value={row.price} onChange={(event) => setShippingItem(provider, index, "price", event.target.value)} />
                    <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => removeShippingItem(provider, index)}><TrashIcon /></button>
                  </div>
                ))}
              </div>
              <button type="button" className="button button-secondary" onClick={() => setForm((current) => ({ ...current, shippingPrices: { ...current.shippingPrices, [provider]: [...current.shippingPrices[provider], { ...emptyShipping }] } }))}>+ Zeile</button>
            </div>
          ))}

          <div className="field field-wide">
            <span>Sonstiges</span>
            {form.miscBillingData.map((row, index) => (
              <div className="inline-actions" key={index}>
                <input placeholder="Bezeichnung" value={row.label} onChange={(event) => setArrayItem("miscBillingData", index, "label", event.target.value)} />
                <input placeholder="Wert" value={row.value} onChange={(event) => setArrayItem("miscBillingData", index, "value", event.target.value)} />
                <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => removeArrayItem("miscBillingData", index, { label: "", value: "" })}><TrashIcon /></button>
              </div>
            ))}
            <button type="button" className="button button-secondary" onClick={() => addArrayItem("miscBillingData", { label: "", value: "" })}>+ Sonstiges</button>
          </div>
          </>
          )}

          {activeSection === "contract" && (
          <>
          <div className="field field-wide customer-contract-documents">
            <span>Dokumente</span>
            <div className="inline-actions">
              {["Vertrag", "Anlage", "Sepamandat"].map((label) => (
                <label key={label} className="button button-secondary">
                  {label}
                  <input type="file" accept="application/pdf" className="visually-hidden" onChange={(event) => handleContractUpload(event, label)} />
                </label>
              ))}
              <button type="button" className="button button-secondary" onClick={() => setShowMoreDocuments((value) => !value)}>
                Weitere {showMoreDocuments ? "▴" : "▾"}
              </button>
            </div>
            {form.contractFiles.map((file, index) => (
              <div className="inline-actions" key={`${file.fileName}-${index}`}>
                <input value={file.label || ""} onChange={(event) => setArrayItem("contractFiles", index, "label", event.target.value)} />
                <span>{file.fileName}</span>
                <button type="button" className="button button-secondary" onClick={() => downloadDataUrl(file.data, getSupplierAttachmentName(form, getContractAttachmentType(file.label), file.data, getContractSequence(form.contractFiles, index)))}>Download</button>
                <button type="button" className="button button-inline-danger button-icon-danger compact-delete" aria-label="Loeschen" title="Loeschen" onClick={() => setForm((current) => ({ ...current, contractFiles: current.contractFiles.filter((item, itemIndex) => itemIndex !== index) }))}><TrashIcon /></button>
              </div>
            ))}
          </div>

          {showMoreDocuments && (
          <>
          <label className="field">
            <span>Traces Import</span>
            <select value={form.tracesImport ? "ja" : "nein"} onChange={(event) => setForm({ ...form, tracesImport: event.target.value === "ja" })}>
              <option value="nein">Nein</option>
              <option value="ja">Ja</option>
            </select>
          </label>
          <label className="field">
            <span>Zertifikat gueltig bis</span>
            <input type="month" value={form.certificateValidUntil} onChange={(event) => setForm({ ...form, certificateValidUntil: event.target.value })} />
          </label>
          <div className="field"><span>Zertifikat vorhanden</span><div className="message message-info file-status">{form.certificateData ? "Ja" : "Nein"}</div></div>
          <div className="field field-wide">
            <span>BIO Zertifikat</span>
            <div className="capture-card">
              <strong>{form.certificateData ? "BIO Zertifikat vorhanden" : "Kein BIO Zertifikat hochgeladen"}</strong>
              <div className="inline-actions">
                <input ref={certificateInputRef} type="file" accept="application/pdf" className="visually-hidden" onChange={(event) => handlePdfUpload(event, "certificateData")} />
                <button type="button" onClick={() => certificateInputRef.current?.click()} className="button button-secondary">PDF hochladen</button>
                {form.certificateData && <button type="button" onClick={() => downloadDataUrl(form.certificateData, getSupplierAttachmentName(form, "ZERTIFIKAT", form.certificateData))} className="button button-inline-edit">Download</button>}
                {form.certificateData && <button type="button" onClick={() => setForm({ ...form, certificateData: "", certificateValidUntil: "" })} className="button button-inline-danger">Entfernen</button>}
              </div>
            </div>
          </div>
          <label className="field">
            <span>Name sonstige Datei</span>
            <input value={form.otherFileName} placeholder="Dateiname fuer Download" onChange={(event) => setForm({ ...form, otherFileName: event.target.value })} />
          </label>
          <div className="field field-wide">
            <span>Sonstige PDF-Datei</span>
            <div className="capture-card">
              <strong>{form.otherFileData ? "Sonstige PDF vorhanden" : "Keine sonstige PDF hochgeladen"}</strong>
              <div className="inline-actions">
                <input ref={otherFileInputRef} type="file" accept="application/pdf" className="visually-hidden" onChange={(event) => handlePdfUpload(event, "otherFileData")} />
                <button type="button" onClick={() => otherFileInputRef.current?.click()} className="button button-secondary">PDF hochladen</button>
                {form.otherFileData && <button type="button" onClick={() => downloadDataUrl(form.otherFileData, getSupplierAttachmentName(form, "DOKUMENT", form.otherFileData))} className="button button-inline-edit">Download</button>}
                {form.otherFileData && <button type="button" onClick={() => setForm({ ...form, otherFileData: "", otherFileName: "" })} className="button button-inline-danger">Entfernen</button>}
              </div>
            </div>
          </div>
          </>
          )}
          </>
          )}
        </div>

        <div className="button-row">
          <button onClick={handleSave} className="button button-primary">
            {editId ? "Aenderungen speichern" : "Kunde speichern"}
          </button>
          <button onClick={() => resetCustomerForm("master")} className="button button-secondary">
            Formular leeren
          </button>
        </div>
      </section>
      )}

      {activeSection === "customers" && (
      <section className="card">
        <div className="section-head">
          <h2>Kundenliste</h2>
          <button type="button" className="button button-secondary customer-close-button" aria-label="Aktiven Kunden abwählen" title="Aktiven Kunden abwählen" onClick={() => resetCustomerForm("customers")}>×</button>
        </div>
        <label className="field customer-search-field">
          <span>Kunden durchsuchen</span>
          <input value={customerSearch} placeholder="Firma, Kürzel, JTL-Nummer oder Ansprechpartner" onChange={(event) => setCustomerSearch(event.target.value)} />
        </label>
        <div className="table-wrap">
          <table className="data-table supplier-table">
            <thead>
              <tr>
                <th>Kuerzel</th>
                <th>Kundennummer JTL</th>
                <th>Name Firma</th>
                <th>Ansprechpartner</th>
                <th>Email allgemein</th>
                <th>Abrechnung</th>
                <th>Paketkunde</th>
                <th>Bio</th>
                <th>Zertifikat</th>
                <th>Traces</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filterSuppliers(suppliers, customerSearch).map((supplier) => (
                <tr key={supplier.id}>
                  <td>{supplier.short}</td>
                  <td>{supplier.customerNumber || "-"}</td>
                  <td>{supplier.name}</td>
                  <td>{supplier.contact || "-"}</td>
                  <td>{supplier.generalEmail || supplier.phone || "-"}</td>
                  <td>{supplier.billingPeriod || "-"}</td>
                  <td>{supplier.isPackageCustomer ? supplier.packageType || "Ja" : "Nein"}</td>
                  <td>{getBioLabel(supplier)}</td>
                  <td>{supplier.certificateAvailable ? "Ja" : "Nein"}</td>
                  <td>{supplier.tracesImport ? "Ja" : "Nein"}</td>
                  <td>
                    <div className="inline-actions">
                      <button onClick={() => handleEdit(supplier)} className="button button-inline-edit button-icon-edit" aria-label="Bearbeiten" title="Bearbeiten"><EditIcon /></button>
                      {supplier.certificateData && <button type="button" onClick={() => downloadDataUrl(supplier.certificateData, getSupplierAttachmentName(supplier, "ZERTIFIKAT", supplier.certificateData))} className="button button-secondary">BIO PDF</button>}
                      {supplier.otherFileData && <button type="button" onClick={() => downloadDataUrl(supplier.otherFileData, getSupplierAttachmentName(supplier, "DOKUMENT", supplier.otherFileData))} className="button button-secondary">PDF</button>}
                      <button onClick={() => handleDelete(supplier.id)} className="button button-inline-danger button-icon-danger" aria-label="Loeschen" title="Loeschen"><TrashIcon /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
}
